import { ThemeProvider, CssBaseline } from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import Layout from './components/Layout';
import Home from './components/pages/Home';
import Builders from './components/pages/Builders';
import ProductsAndServices from './components/pages/ProductsAndServices';
import Support from './components/pages/Support';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import CookiesPolicy from './components/pages/CookiesPolicy';
import Enquiry from './components/pages/Enquiry';
import TermsAndConditions from './components/pages/TermsAndConditions';

const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#0066b2',
      light: '#3384c7',
      dark: '#004c8c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#2c3e50',
      light: '#435466',
      dark: '#1a252f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fbff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2b3c',
      secondary: '#445668',
    },
    divider: 'rgba(0, 102, 178, 0.1)',
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#0066b2',
      fontFamily: '"Poppins", "Roboto", sans-serif',
    },
    h2: {
      fontWeight: 600,
      color: '#1a2b3c',
      fontFamily: '"Poppins", "Roboto", sans-serif',
    },
    h5: {
      fontWeight: 500,
      color: '#445668',
      fontFamily: '"Poppins", "Roboto", sans-serif',
    },
    h6: {
      fontWeight: 500,
      letterSpacing: '0.0075em',
      fontFamily: '"Poppins", "Roboto", sans-serif',
    },
    body1: {
      lineHeight: 1.7,
      letterSpacing: '0.00938em',
      color: '#445668',
    },
    body2: {
      lineHeight: 1.6,
      letterSpacing: '0.01071em',
      color: '#546e7a',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 8px 32px rgba(0, 102, 178, 0.08)',
          borderRadius: '16px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '1rem',
          padding: '12px 28px',
          boxShadow: '0 4px 12px rgba(0, 102, 178, 0.15)',
          fontFamily: '"Poppins", "Roboto", sans-serif',
          fontWeight: 500,
          '&:hover': {
            boxShadow: '0 8px 20px rgba(0, 102, 178, 0.25)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          background: 'linear-gradient(135deg, #0066b2 0%, #004c8c 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0075cc 0%, #005ca6 100%)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            '&:hover fieldset': {
              borderColor: '#0066b2',
              borderWidth: '2px',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0066b2',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#445668',
            '&.Mui-focused': {
              color: '#0066b2',
            },
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 1200px)': {
            maxWidth: '1200px',
          },
        },
      },
    },
  },
});

// Apply responsive font sizes and create the final theme
const theme = responsiveFontSizes(baseTheme, {
  breakpoints: ['xs', 'sm', 'md'],
  factor: 0.5,
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Layout />
      </Router>
    </ThemeProvider>
  );
}

export default App;
